import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTenant } from "../context/TenantContext";
import { useLabels } from "../utils/labels";
import MoreFields from "./MoreFields";
import {
  Box,
  Drawer,
  IconButton,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
  Divider,
  Chip,
  Button,
  Paper,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import {
  MdClose,
  MdPerson,
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdCalendarToday,
  MdBloodtype,
  MdWork,
  MdFamilyRestroom,
  MdLanguage,
  MdWarning,
  MdLocalHospital,
  MdEmergency,
  MdCardMembership,
  MdAssignment,
  MdNote,
  MdAttachFile,
  MdDelete,
  MdSave,
} from "react-icons/md";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const validationSchema = Yup.object().shape({
  full_name: Yup.string().required("Full name is required"),
  contact: Yup.string()
    .required("Contact number is required")
    .matches(/^(?:(?:\+|0{0,2})91|[0])?[6789]\d{9}$/, "Please enter valid phone number"),
  email: Yup.string().email("Invalid email address"),
  emergency_contact_phone: Yup.string().matches(
    /^(?:(?:\+|0{0,2})91|[0])?[6789]\d{9}$/,
    "Please enter valid phone number"
  ),
});

const PatientSidebarForm = ({ open, onClose, mode = "add", patientData = null, onSubmit }) => {
  const { tenant } = useTenant();
  const labels = useLabels();
  const [activeTab, setActiveTab] = useState(0);
  const [customFields, setCustomFields] = useState({});
  useEffect(() => {
    if (open) setCustomFields({});
  }, [open, patientData?.patient_id]);
  const [prescriptionFiles, setPrescriptionFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const initialValues = {
    // Basic Information
    full_name: patientData?.full_name || "",
    date_of_birth: patientData?.date_of_birth ? dayjs(patientData.date_of_birth) : null,
    age: patientData?.age || "",
    gender: patientData?.gender || "",
    blood_group: patientData?.blood_group || "",
    patient_id_number: patientData?.patient_id_number || "",

    // Contact Information
    contact: patientData?.contact || "",
    alternate_contact: patientData?.alternate_contact || "",
    email: patientData?.email || "",
    address: patientData?.address || "",
    city: patientData?.city || "",
    state: patientData?.state || "",
    zip_code: patientData?.zip_code || "",
    country: patientData?.country || "India",

    // Personal Information
    occupation: patientData?.occupation || "",
    marital_status: patientData?.marital_status || "",
    nationality: patientData?.nationality || "Indian",
    language_preference: patientData?.language_preference || "English",

    // Medical Information
    allergies: patientData?.allergies || "",
    medical_history: patientData?.medical_history || "",
    current_medications: patientData?.current_medications || "",
    chronic_conditions: patientData?.chronic_conditions || "",

    // Emergency Contact
    emergency_contact_name: patientData?.emergency_contact_name || "",
    emergency_contact_relation: patientData?.emergency_contact_relation || "",
    emergency_contact_phone: patientData?.emergency_contact_phone || "",

    // Insurance Information
    insurance_provider: patientData?.insurance_provider || "",
    insurance_policy_number: patientData?.insurance_policy_number || "",
    insurance_group_number: patientData?.insurance_group_number || "",

    // Hospital Information
    referred_by: patientData?.referred_by || "",
    registration_date: patientData?.registration_date ? dayjs(patientData.registration_date) : dayjs(),
    notes: patientData?.notes || "",
    is_active: patientData?.is_active !== undefined ? patientData.is_active : 1,
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    setUploadingFiles(true);
    
    // Here you would upload files to server
    // For now, we'll just add them to state
    const newFiles = files.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      file: file,
      uploaded: false,
    }));
    
    setPrescriptionFiles([...prescriptionFiles, ...newFiles]);
    setUploadingFiles(false);
  };

  const handleRemoveFile = (fileId) => {
    setPrescriptionFiles(prescriptionFiles.filter((f) => f.id !== fileId));
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      // Format dates (only if they exist)
      const formattedValues = {
        ...values,
        ...(values.date_of_birth ? { date_of_birth: values.date_of_birth.format("YYYY-MM-DD") } : {}),
        ...(values.registration_date ? { registration_date: values.registration_date.format("YYYY-MM-DD") } : {}),
        prescriptions: prescriptionFiles,
        custom_fields: customFields,
      };

      await onSubmit(formattedValues);
      // Only clear files and close on success (onClose is called by parent after successful submission)
      setPrescriptionFiles([]);
    } catch (error) {
      console.error("Error submitting form:", error);
      // Show error message to user
      if (error?.response?.data?.error?.message) {
        // If there's a field-specific error, set it
        const errorMessage = error.response.data.error.message;
        if (errorMessage.includes('contact') || errorMessage.includes('phone')) {
          setFieldError('contact', errorMessage);
        } else if (errorMessage.includes('email')) {
          setFieldError('email', errorMessage);
        } else if (errorMessage.includes('name')) {
          setFieldError('full_name', errorMessage);
        }
      }
      // Don't close sidebar on error - let user fix and retry
    } finally {
      setSubmitting(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const today = dayjs();
    const birthDate = dayjs(dateOfBirth);
    return today.diff(birthDate, "year");
  };

  // Check if this is a clinic industry
  const isClinic = tenant?.industry_key === 'clinic' || tenant?.industry === 'clinic';
  
  // Build tabs based on industry config
  const buildTabs = () => {
    // For non-clinic industries, only show one tab with all fields
    if (!isClinic) {
      return [
        { label: "Customer Information", icon: <MdPerson />, key: "basic" },
      ];
    }
    
    // For clinic, show all tabs based on config
    const baseTabs = [
      { label: "Basic Info", icon: <MdPerson />, key: "basic" },
      { label: "Contact", icon: <MdPhone />, key: "contact" },
    ];
    
    // Check if medical features are disabled
    const disabledFeatures = tenant?.config?.disabled_features || [];
    const isMedicalDisabled = disabledFeatures.includes("medical_history") || 
                              disabledFeatures.includes("prescriptions");
    const isInsuranceDisabled = disabledFeatures.includes("insurance");
    
    if (!isMedicalDisabled) {
      baseTabs.push({ label: "Medical", icon: <MdLocalHospital />, key: "medical" });
    }
    
    baseTabs.push({ label: "Emergency", icon: <MdEmergency />, key: "emergency" });
    
    if (!isInsuranceDisabled) {
      baseTabs.push({ label: "Insurance", icon: <MdCardMembership />, key: "insurance" });
    }
    
    baseTabs.push(
      { label: "Documents", icon: <MdAttachFile />, key: "documents" },
      { label: "Notes", icon: <MdNote />, key: "notes" }
    );
    
    return baseTabs;
  };
  
  const tabs = buildTabs();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "90%", md: "600px", lg: "700px" },
          maxWidth: "700px",
        },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, isSubmitting, handleChange }) => (
          <Form>
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              {/* Header */}
              <Box
                sx={{
                  p: 3,
                  borderBottom: "1px solid #e0e0e0",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                    {mode === "add" ? `Add New ${labels.customer || "Customer"}` : `Edit ${labels.customer || "Customer"}`}
                  </Typography>
                  <IconButton onClick={onClose} sx={{ color: "white" }}>
                    <MdClose />
                  </IconButton>
                </Box>
                {/* Only show tabs if there's more than one tab */}
                {tabs.length > 1 && (
                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      "& .MuiTab-root": {
                        color: "rgba(255,255,255,0.7)",
                        minHeight: "48px",
                        fontSize: "0.75rem",
                      },
                      "& .Mui-selected": {
                        color: "white",
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: "white",
                      },
                    }}
                  >
                    {tabs.map((tab, index) => (
                      <Tab key={tab.key || index} label={tab.label} icon={tab.icon} iconPosition="start" />
                    ))}
                  </Tabs>
                )}
              </Box>

              {/* Content */}
              <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
                <AnimatePresence mode="wait">
                  {tabs[activeTab]?.key === "basic" && (
                    <motion.div
                      key="basic"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#667eea" }}>
                        {isClinic ? "Basic Information" : `${labels.customer || "Customer"} Information`}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                          name="full_name"
                          label="Full Name *"
                          value={values.full_name}
                          onChange={handleChange}
                          error={touched.full_name && !!errors.full_name}
                          helperText={touched.full_name && errors.full_name || `Enter ${labels.customer?.toLowerCase() || "customer"}'s full legal name`}
                          placeholder="e.g., Rajesh Kumar"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdPerson color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />

                        {/* For non-clinic: Show contact fields in the same tab */}
                        {!isClinic && (
                          <>
                            <TextField
                              name="email"
                              label="Email Address"
                              type="email"
                              value={values.email}
                              onChange={handleChange}
                              error={touched.email && !!errors.email}
                              helperText={touched.email && errors.email || "e.g., customer@example.com"}
                              placeholder="e.g., customer@example.com"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <MdEmail color="#667eea" />
                                  </InputAdornment>
                                ),
                              }}
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': {
                                    borderColor: '#667eea',
                                  },
                                },
                              }}
                            />

                            <TextField
                              name="contact"
                              label="Primary Contact *"
                              value={values.contact}
                              onChange={handleChange}
                              error={touched.contact && !!errors.contact}
                              helperText={touched.contact && errors.contact || "10-digit mobile number"}
                              placeholder="e.g., 9876543210"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <MdPhone color="#667eea" />
                                  </InputAdornment>
                                ),
                              }}
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': {
                                    borderColor: '#667eea',
                                  },
                                },
                              }}
                            />

                            <TextField
                              name="alternate_contact"
                              label="WhatsApp Number"
                              value={values.alternate_contact}
                              onChange={handleChange}
                              placeholder="e.g., 9876543211"
                              helperText="WhatsApp number for communication (optional)"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <MdPhone color="#667eea" />
                                  </InputAdornment>
                                ),
                              }}
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': {
                                    borderColor: '#667eea',
                                  },
                                },
                              }}
                            />

                            <TextField
                              name="address"
                              label="Address"
                              multiline
                              rows={2}
                              value={values.address}
                              onChange={handleChange}
                              placeholder="e.g., 123 Main Street, Andheri"
                              helperText="Street address (optional)"
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <MdLocationOn color="#667eea" />
                                  </InputAdornment>
                                ),
                              }}
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  '&:hover fieldset': {
                                    borderColor: '#667eea',
                                  },
                                },
                              }}
                            />

                            <Box sx={{ display: "flex", gap: 2 }}>
                              <TextField
                                name="city"
                                label="City"
                                value={values.city}
                                onChange={handleChange}
                                fullWidth
                              />
                              <TextField
                                name="state"
                                label="State"
                                value={values.state}
                                onChange={handleChange}
                                fullWidth
                              />
                            </Box>

                            <Box sx={{ display: "flex", gap: 2 }}>
                              <TextField
                                name="zip_code"
                                label="Zip Code"
                                value={values.zip_code}
                                onChange={handleChange}
                                fullWidth
                              />
                              <TextField
                                name="country"
                                label="Country"
                                value={values.country}
                                onChange={handleChange}
                                fullWidth
                              />
                            </Box>
                          </>
                        )}

                        {/* Clinic-specific fields */}
                        {isClinic && (
                          <>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Date of Birth"
                            value={values.date_of_birth}
                            onChange={(newValue) => {
                              setFieldValue("date_of_birth", newValue);
                              if (newValue) {
                                setFieldValue("age", calculateAge(newValue));
                              }
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <MdCalendarToday color="#667eea" />
                                    </InputAdornment>
                                  ),
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>

                        <TextField
                          name="age"
                          label="Age"
                          type="number"
                          value={values.age}
                          onChange={handleChange}
                          placeholder="Auto-calculated from date of birth"
                          helperText="Automatically calculated from date of birth"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdCalendarToday color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                          disabled
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#f9fafb',
                            },
                          }}
                        />

                        <TextField
                          name="gender"
                          label="Gender"
                          select
                          value={values.gender}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdPerson color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        >
                          <MenuItem value="">Select Gender</MenuItem>
                          <MenuItem value="male">Male</MenuItem>
                          <MenuItem value="female">Female</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                          <MenuItem value="prefer_not_to_say">Prefer Not to Say</MenuItem>
                        </TextField>

                        <TextField
                          name="blood_group"
                          label="Blood Group"
                          select
                          value={values.blood_group}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdBloodtype color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        >
                          <MenuItem value="">Select Blood Group</MenuItem>
                          <MenuItem value="A+">A+</MenuItem>
                          <MenuItem value="A-">A-</MenuItem>
                          <MenuItem value="B+">B+</MenuItem>
                          <MenuItem value="B-">B-</MenuItem>
                          <MenuItem value="AB+">AB+</MenuItem>
                          <MenuItem value="AB-">AB-</MenuItem>
                          <MenuItem value="O+">O+</MenuItem>
                          <MenuItem value="O-">O-</MenuItem>
                        </TextField>

                        <TextField
                          name="patient_id_number"
                          label={`${labels.customer || "Customer"} ID Number`}
                          value={values.patient_id_number}
                          onChange={handleChange}
                          placeholder="e.g., PAT001234"
                          helperText={`Unique identifier for this ${labels.customer?.toLowerCase() || "customer"} (optional)`}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />
                            </>
                          )}
                      </Box>
                    </motion.div>
                  )}

                  {tabs[activeTab]?.key === "contact" && (
                    <motion.div
                      key="contact"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#667eea" }}>
                        Contact Information
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                          name="contact"
                          label="Primary Contact *"
                          value={values.contact}
                          onChange={handleChange}
                          error={touched.contact && !!errors.contact}
                          helperText={touched.contact && errors.contact || "10-digit mobile number"}
                          placeholder="e.g., 9876543210"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdPhone color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />

                        <TextField
                          name="alternate_contact"
                          label={isClinic ? "Alternate Contact" : "WhatsApp Number"}
                          value={values.alternate_contact}
                          onChange={handleChange}
                          placeholder="e.g., 9876543211"
                          helperText={isClinic ? "Alternative contact number (optional)" : "WhatsApp number for communication (optional)"}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdPhone color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />

                        <TextField
                          name="email"
                          label="Email Address"
                          type="email"
                          value={values.email}
                          onChange={handleChange}
                          error={touched.email && !!errors.email}
                          helperText={touched.email && errors.email || "e.g., patient@example.com"}
                          placeholder="e.g., patient@example.com"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdEmail color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />

                        <TextField
                          name="address"
                          label="Address"
                          multiline
                          rows={2}
                          value={values.address}
                          onChange={handleChange}
                          placeholder="e.g., 123 Main Street, Andheri"
                          helperText="Street address (optional)"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdLocationOn color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#667eea',
                              },
                            },
                          }}
                        />

                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField
                            name="city"
                            label="City"
                            value={values.city}
                            onChange={handleChange}
                            fullWidth
                          />
                          <TextField
                            name="state"
                            label="State"
                            value={values.state}
                            onChange={handleChange}
                            fullWidth
                          />
                        </Box>

                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField
                            name="zip_code"
                            label="Zip Code"
                            value={values.zip_code}
                            onChange={handleChange}
                            fullWidth
                          />
                          <TextField
                            name="country"
                            label="Country"
                            value={values.country}
                            onChange={handleChange}
                            fullWidth
                          />
                        </Box>
                      </Box>
                    </motion.div>
                  )}

                  {tabs[activeTab]?.key === "medical" && (
                    <motion.div
                      key="medical"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#667eea" }}>
                        Medical Information
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                          name="allergies"
                          label="Allergies"
                          multiline
                          rows={3}
                          value={values.allergies}
                          onChange={handleChange}
                          placeholder="List any known allergies (e.g., Penicillin, Peanuts)"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdWarning color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        />

                        <TextField
                          name="medical_history"
                          label="Medical History"
                          multiline
                          rows={4}
                          value={values.medical_history}
                          onChange={handleChange}
                          placeholder="Past medical history, surgeries, hospitalizations"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdLocalHospital color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        />

                        <TextField
                          name="current_medications"
                          label="Current Medications"
                          multiline
                          rows={3}
                          value={values.current_medications}
                          onChange={handleChange}
                          placeholder="List current medications with dosages"
                          fullWidth
                        />

                        <TextField
                          name="chronic_conditions"
                          label="Chronic Conditions"
                          multiline
                          rows={3}
                          value={values.chronic_conditions}
                          onChange={handleChange}
                          placeholder="Chronic diseases (e.g., Diabetes, Hypertension)"
                          fullWidth
                        />

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                          Personal Information
                        </Typography>

                        <TextField
                          name="occupation"
                          label="Occupation"
                          value={values.occupation}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdWork color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        />

                        <TextField
                          name="marital_status"
                          label="Marital Status"
                          select
                          value={values.marital_status}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdFamilyRestroom color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        >
                          <MenuItem value="">Select Marital Status</MenuItem>
                          <MenuItem value="single">Single</MenuItem>
                          <MenuItem value="married">Married</MenuItem>
                          <MenuItem value="divorced">Divorced</MenuItem>
                          <MenuItem value="widowed">Widowed</MenuItem>
                          <MenuItem value="separated">Separated</MenuItem>
                        </TextField>

                        <Box sx={{ display: "flex", gap: 2 }}>
                          <TextField
                            name="nationality"
                            label="Nationality"
                            value={values.nationality}
                            onChange={handleChange}
                            fullWidth
                          />
                          <TextField
                            name="language_preference"
                            label="Language Preference"
                            value={values.language_preference}
                            onChange={handleChange}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <MdLanguage color="#667eea" />
                                </InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        </Box>
                      </Box>
                    </motion.div>
                  )}

                  {tabs[activeTab]?.key === "emergency" && (
                    <motion.div
                      key="emergency"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#667eea" }}>
                        Emergency Contact
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                          name="emergency_contact_name"
                          label="Emergency Contact Name"
                          value={values.emergency_contact_name}
                          onChange={handleChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdPerson color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        />

                        <TextField
                          name="emergency_contact_relation"
                          label="Relationship"
                          value={values.emergency_contact_relation}
                          onChange={handleChange}
                          placeholder="e.g., Spouse, Parent, Sibling"
                          fullWidth
                        />

                        <TextField
                          name="emergency_contact_phone"
                          label="Emergency Contact Phone"
                          value={values.emergency_contact_phone}
                          onChange={handleChange}
                          error={touched.emergency_contact_phone && !!errors.emergency_contact_phone}
                          helperText={touched.emergency_contact_phone && errors.emergency_contact_phone}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdEmergency color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        />
                      </Box>
                    </motion.div>
                  )}

                  {tabs[activeTab]?.key === "insurance" && (
                    <motion.div
                      key="insurance"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#667eea" }}>
                        Insurance Information
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                          name="insurance_provider"
                          label="Insurance Provider"
                          value={values.insurance_provider}
                          onChange={handleChange}
                          placeholder="Name of insurance company"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdCardMembership color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        />

                        <TextField
                          name="insurance_policy_number"
                          label="Policy Number"
                          value={values.insurance_policy_number}
                          onChange={handleChange}
                          fullWidth
                        />

                        <TextField
                          name="insurance_group_number"
                          label="Group Number"
                          value={values.insurance_group_number}
                          onChange={handleChange}
                          fullWidth
                        />
                      </Box>
                    </motion.div>
                  )}

                  {tabs[activeTab]?.key === "documents" && (
                    <motion.div
                      key="documents"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#667eea" }}>
                        Prescriptions & Documents
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Paper
                          sx={{
                            p: 3,
                            border: "2px dashed #667eea",
                            borderRadius: 2,
                            textAlign: "center",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(102, 126, 234, 0.05)",
                            },
                          }}
                          onClick={() => document.getElementById("prescription-upload").click()}
                        >
                          <input
                            id="prescription-upload"
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleFileUpload}
                          />
                          <MdAttachFile size={40} color="#667eea" style={{ margin: "0 auto 10px" }} />
                          <Typography variant="body2" color="text.secondary">
                            Click to upload prescriptions (PDF, JPG, PNG)
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Multiple files supported
                          </Typography>
                        </Paper>

                        {prescriptionFiles.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                              Uploaded Files ({prescriptionFiles.length})
                            </Typography>
                            {prescriptionFiles.map((file) => (
                              <Chip
                                key={file.id}
                                label={file.name}
                                onDelete={() => handleRemoveFile(file.id)}
                                deleteIcon={<MdDelete />}
                                sx={{ m: 0.5 }}
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </motion.div>
                  )}

                  {tabs[activeTab]?.key === "notes" && (
                    <motion.div
                      key="notes"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold", color: "#667eea" }}>
                        Additional Information
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                          name="referred_by"
                          label="Referred By"
                          value={values.referred_by}
                          onChange={handleChange}
                          placeholder={`Doctor or person who referred the ${labels.customer?.toLowerCase() || "customer"}`}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdAssignment color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        />

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Registration Date"
                            value={values.registration_date}
                            onChange={(newValue) => setFieldValue("registration_date", newValue)}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <MdCalendarToday color="#667eea" />
                                    </InputAdornment>
                                  ),
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>

                        <TextField
                          name="notes"
                          label="Additional Notes"
                          multiline
                          rows={6}
                          value={values.notes}
                          onChange={handleChange}
                          placeholder={`Any additional notes about the ${labels.customer?.toLowerCase() || "customer"}`}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MdNote color="#667eea" />
                              </InputAdornment>
                            ),
                          }}
                          fullWidth
                        />

                        <TextField
                          name="is_active"
                          label="Status"
                          select
                          value={values.is_active}
                          onChange={handleChange}
                          fullWidth
                        >
                          <MenuItem value={1}>Active</MenuItem>
                          <MenuItem value={0}>Inactive</MenuItem>
                        </TextField>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
                <MoreFields
                  module="customers"
                  recordId={mode === "edit" ? patientData?.patient_id : null}
                  values={customFields}
                  onChange={setCustomFields}
                />
              </Box>

              {/* Footer */}
              <Box
                sx={{
                  p: 3,
                  borderTop: "1px solid #e0e0e0",
                  display: "flex",
                  gap: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={<MdSave />}
                  sx={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)",
                    },
                  }}
                >
                  {isSubmitting ? "Saving..." : mode === "add" ? `Add ${labels.customer || "Customer"}` : `Update ${labels.customer || "Customer"}`}
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

export default PatientSidebarForm;

